import { useCallback, useEffect, useState } from "react";
import { Box, Typography, Paper } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Outlet, useLocation, useParams } from "react-router-dom";
import { useProperty } from "../../../context/PropertyContext";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import { getPropertyById } from "../../../services/propertyService";
import { trackPropertyView } from "../../../services/propertyViewService";
import { PulseLoader } from "react-spinners";
import CustomAppBar from "../../../components/shared/CustomAppBar";
import PropertyHeader from "../../../components/propertyDetailChildren/PropertyHeader";
import PropertyGallery from "../../../components/propertyDetailChildren/PropertyGallery";
import PropertyContactCard from "../../../components/propertyDetailChildren/PropertyContactCard";
import PropertyMapWrapper from "../../../components/propertyDetailChildren/PropertyMap";
import PropertyHunterInfo from "../../../components/propertyDetailChildren/PropertyHunterInfo";
import PropertyEmbeddedChat from "../../../components/chat/EmbeddedChat";
import ShareModal from "../../../components/propertyDetailChildren/ShareModal";
import AuthModal from "../../../components/modals/AuthModal";
import WelcomeBackModal from "../../../components/modals/WelcomeBackModal";
import RentalModal from "../../../components/modals/RentalModal";
import PurchaseModal from "../../../components/modals/PurchaseModal";
import { CollaborationSection } from "../../../components/propertyDetailChildren/BrokerCollaborationSection";
import PropertyEditSection from "./PropertyEditSection";
import PropertyDetails from "../../../components/propertyDetailChildren/PropertyDetails";

const PropertyDetailPage = () => {
  const { selectedProperty, handleSetSelectedProperty, toggleFavorite } =
    useProperty();
  const [openModal, setOpenModal] = useState(false);
  const [openWelcomeBackModal, setOpenWelcomeBackModal] = useState(false);
  const navigate = useNavigate();
  const [isOutletVisible, setIsOutletVisible] = useState(false);
  const location = useLocation();
  const cameFromBrokerProfile = location.state?.fromBrokerProfile || false;
  const {
    tokenKey,
    wasLoggedIn,
    decodeIdToken,
    isHunter,
    isBroker,
    userToken,
  } = useAuth();
  const [openAuthModal, setOpenAuthModal] = useState(false);
  const [openRentalModal, setOpenRentalModal] = useState(false);
  const [openPurchaseModal, setOpenPurchaseModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { id } = useParams();

  const fetchData = useCallback(async () => {
    try {
      const fetchedProperty = await getPropertyById(id ?? "0");
      handleSetSelectedProperty(fetchedProperty);

      const trackKey = `viewed-property-${fetchedProperty.id_Property}`;
      if (!sessionStorage.getItem(trackKey)) {
        try {
          await trackPropertyView(fetchedProperty.id_Property);
          sessionStorage.setItem(trackKey, "true");
        } catch (trackErr) {
          console.warn("Error al trackear la vista de propiedad:", trackErr);
        }
      }
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [id, handleSetSelectedProperty]);

  useEffect(() => {
    if (id) {
      fetchData();
    }
  }, [id, fetchData]);

  useEffect(() => {
    if (location?.pathname) {
      const isVisible = location.pathname.includes("/photos");
      setIsOutletVisible(isVisible);
    }
  }, [location?.pathname]);

  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => setOpenModal(false);

  const handleAuthModalOpen = () => {
    setOpenAuthModal(true);
  };

  const handleAuthModalClose = () => {
    setOpenAuthModal(false);
  };

  const handleWelcomeBackModalOpen = () => {
    const googleToken = localStorage.getItem("googleIdToken");
    decodeIdToken(googleToken!);
    setOpenWelcomeBackModal(true);
  };

  const handleWelcomeBackModalClose = () => {
    setOpenWelcomeBackModal(false);
  };

  const handleRentalModalClose = () => {
    setOpenRentalModal(false);
  };

  const handlePurchaseModalClose = () => {
    setOpenPurchaseModal(false);
  };

  const handleDynamicButtonClick = (type: string) => {
    if (tokenKey) {
      if (type === "Alquiler") {
        setOpenRentalModal(true);
      } else {
        setOpenPurchaseModal(true);
      }
    } else {
      if (wasLoggedIn) {
        handleWelcomeBackModalOpen();
      } else {
        handleAuthModalOpen();
      }
    }
  };

  const handleViewPhotos = () => {
    navigate(
      `/properties/property-detail/${selectedProperty!.id_Property}/photos`
    );
    setIsOutletVisible(true);
  };

  const handleViewProfile = (id?: number) => {
    if (!id) return;
    if (cameFromBrokerProfile) {
      return;
    }
    navigate(`/properties/realtor/${id}`, {
      state: { fromPropertyId: selectedProperty!.id_Property },
    });
  };

  const handleViewPDF = () => {
    navigate("/properties/pdfpreview", {
      state: { property: selectedProperty },
    });
  };

  const handleFavorite = (event: React.MouseEvent) => {
    if (tokenKey) {
      toggleFavorite(selectedProperty!.id_Property);
    }
  };

  const Broker = userToken?.user.profiles.find(
    (profile: any) => profile.roleName.toLowerCase() === "broker"
  );
  const isCurrentUserBroker = selectedProperty?.id_Realtor === Broker?.realtor?.id;

  const isFromSameTeam = Broker?.realtor?.teamId && Broker?.realtor?.teamId === selectedProperty?.id_Realtor_Team;

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: "#fafafa",
        }}
      >
        <PulseLoader color="#36d7b7" size={15} margin={2} />
        <Typography
          variant="h6"
          color="textSecondary"
          sx={{ mt: 3, textAlign: "center" }}
        >
          Cargando propiedad, por favor espera...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          textAlign: "center",
          gap: 2,
          backgroundColor: "#fafafa",
        }}
      >
        <img
          src="/assets/images/error-image.png"
          alt="Error"
          style={{ width: "250px", height: "200px" }}
        />
        <Typography variant="h6" color="error">
          Ocurrió un error
        </Typography>
        <Typography variant="body1" color="textSecondary">
          No pudimos obtener la propiedad. Por favor, inténtalo nuevamente más
          tarde.
        </Typography>
      </Box>
    );
  }

  if (!selectedProperty) return <div></div>;

  return (
    <>
      <CustomAppBar />
      <Box px={6} py={2} sx={{ marginTop: "10vh" }}>
        <PropertyHeader
          property={selectedProperty}
          onShare={handleOpenModal}
          onViewPDF={handleViewPDF}
          onFavorite={handleFavorite}
        />

        {isOutletVisible ? (
          <Outlet />
        ) : (
          <>
            <PropertyGallery
              photos={selectedProperty.photos}
              propertyId={selectedProperty.id_Property}
              onViewPhotos={handleViewPhotos}
            />

            <Grid container spacing={2} sx={{ mb: 4 }}>
              <Grid size={{ xs: 12, md: 8 }}>
                {(isCurrentUserBroker || isFromSameTeam) ? (
              <PropertyEditSection property={selectedProperty} />
            ): <PropertyDetails property={selectedProperty} />}
                <Box sx={{ mt: 2 }}>
                  <PropertyEmbeddedChat
                    propertyId={selectedProperty.id_Property}
                    realtorId={selectedProperty.id_Realtor}
                  />
                </Box>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <PropertyContactCard
                  property={selectedProperty}
                  onContactAction={handleDynamicButtonClick}
                  onViewProfile={handleViewProfile}
                  cameFromBrokerProfile={cameFromBrokerProfile}
                />
                <Box sx={{ mt: 2 }}>
                  <CollaborationSection
                    selectedProperty={selectedProperty}
                    userToken={userToken}
                    isBroker={isBroker}
                  />
                </Box>
              </Grid>
            </Grid>

            <Box>
              <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2 }}>
                Dirección
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {selectedProperty.address_Description}
              </Typography>
              <Paper
                elevation={3}
                sx={{
                  height: "400px",
                  width: "100%",
                  borderRadius: "8px",
                  mb: 4,
                }}
              >
                <PropertyMapWrapper
                  latitude={selectedProperty.latitude}
                  longitude={selectedProperty.longitude}
                  price={selectedProperty.price}
                />
              </Paper>
            </Box>

            {selectedProperty.hunterFullName && (isHunter || isBroker) && (
              <PropertyHunterInfo hunter={selectedProperty} />
            )}
          </>
        )}

        {/* Modals */}
        <ShareModal
          open={openModal}
          onClose={handleCloseModal}
          property={selectedProperty}
        />

        {openAuthModal && (
          <AuthModal open={openAuthModal} onClose={handleAuthModalClose} />
        )}

        {openWelcomeBackModal && (
          <WelcomeBackModal
            open={openWelcomeBackModal}
            onClose={handleWelcomeBackModalClose}
            onSignIn={handleAuthModalOpen}
          />
        )}

        {openRentalModal && (
          <RentalModal
            open={openRentalModal}
            onClose={handleRentalModalClose}
          />
        )}

        {openPurchaseModal && (
          <PurchaseModal
            open={openPurchaseModal}
            onClose={handlePurchaseModalClose}
          />
        )}
      </Box>
    </>
  );
};

export default PropertyDetailPage;